"use client";

import { Video } from "@components/Video";
import type { ContentfulAssetFragment, Maybe } from "@server/gql/graphql";
import { Volume2Icon, VolumeXIcon } from "lucide-react";
import type { ComponentPropsWithoutRef, MouseEvent } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { breakpoints } from "@/config/breakpoints";
import { cn } from "@/lib/utils";

type ContentfulVideoProps = ComponentPropsWithoutRef<"video"> & {
  mobileVideo?: Maybe<ContentfulAssetFragment>;
  desktopVideo?: Maybe<ContentfulAssetFragment>;
  withAudioControl?: boolean;
};

const ContentfulVideo = ({
  desktopVideo,
  mobileVideo,
  withAudioControl,
  className,
  ...props
}: ContentfulVideoProps) => {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const [muted, setMuted] = useState(true);
  const [hasAudio, setHasAudio] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mobileVideoSrc = extractVideoSrc(mobileVideo);
  const desktopVideoSrc = extractVideoSrc(desktopVideo);

  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    const [entry] = entries;

    setIsMobile(entry.contentRect.width < breakpoints.lg);
  }, []);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);

    if (document.body) {
      resizeObserver.observe(document.body);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [handleResize]);

  const src =
    isMobile && mobileVideoSrc ? mobileVideoSrc : (desktopVideoSrc ?? null);

  // Only show the mute/unmute control when the video actually carries an audio
  // track. There is no cross-browser standard for this, so we combine the
  // vendor-specific signals that each engine exposes, re-checking until audio
  // has been decoded (Chromium only reports it once playback has started).
  useEffect(() => {
    setHasAudio(false);

    const video = videoRef.current;

    if (!video || !src) {
      return;
    }

    const detectAudio = () => {
      if (videoHasAudioTrack(video)) {
        setHasAudio(true);
        return true;
      }

      return false;
    };

    if (detectAudio()) {
      return;
    }

    video.addEventListener("loadeddata", detectAudio);
    video.addEventListener("playing", detectAudio);

    const intervalId = window.setInterval(() => {
      if (detectAudio()) {
        window.clearInterval(intervalId);
      }
    }, 500);

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 5000);

    return () => {
      video.removeEventListener("loadeddata", detectAudio);
      video.removeEventListener("playing", detectAudio);
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [src]);

  if (!src) {
    return null;
  }

  if (!withAudioControl) {
    return <Video src={src} className={className} {...props} />;
  }

  const toggleMuted = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const next = !muted;
    setMuted(next);
    if (videoRef.current) {
      videoRef.current.muted = next;
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Video ref={videoRef} src={src} {...props} />
      {hasAudio && (
        <button
          type="button"
          onClick={toggleMuted}
          aria-label={muted ? "Unmute video" : "Mute video"}
          aria-pressed={!muted}
          className="absolute bottom-3 right-3 z-[60] inline-grid size-12 place-items-center rounded-full text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)] transition-transform duration-150 ease-out hover:scale-110 focus-visible:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {muted ? (
            <VolumeXIcon className="size-4" />
          ) : (
            <Volume2Icon className="size-4" />
          )}
        </button>
      )}
    </div>
  );
};

ContentfulVideo.displayName = "ContentfulVideo";

export default ContentfulVideo;

function extractVideoSrc(video?: Maybe<ContentfulAssetFragment>) {
  return video?.url;
}

/**
 * Best-effort detection of whether a video element has an audio track.
 *
 * The standard `audioTracks` API is only implemented by Safari, so we fall
 * back to the non-standard signals exposed by Firefox (`mozHasAudio`) and
 * Chromium (`webkitAudioDecodedByteCount`, which only becomes non-zero once
 * audio frames have actually been decoded during playback).
 */
function videoHasAudioTrack(video: HTMLVideoElement) {
  const element = video as HTMLVideoElement & {
    mozHasAudio?: boolean;
    webkitAudioDecodedByteCount?: number;
    audioTracks?: { length: number };
  };

  if (typeof element.mozHasAudio === "boolean") {
    return element.mozHasAudio;
  }

  if (typeof element.webkitAudioDecodedByteCount === "number") {
    return element.webkitAudioDecodedByteCount > 0;
  }

  if (element.audioTracks) {
    return element.audioTracks.length > 0;
  }

  return false;
}
